// Renderer for offer-page elements in preview mode (mobile-optimized).
//
// Extracted from OfferPageBuilderSimple.js. Pure renderer — no setters,
// no toasts, no component-scope state reads. Depends only on its `element`
// argument and the injected `getZoneHeightStyle` helper.
//
// Signature: renderPreviewElement(element, getZoneHeightStyle) → JSX
// Recursive: zone layouts render their children by calling renderPreviewElement
//            again with the same getZoneHeightStyle.
//
// NOTE: source indentation preserved-then-dedented by 2 spaces. Verified
// there are no multi-line template literals where dedenting would alter
// the emitted string (only single-line `${...}` interpolations present).

import React from 'react';
import { Box, Typography } from '@mui/material';
import {
  ArrowForward as ArrowForwardIcon,
  Lock as LockIcon,
  ShoppingCart as ShoppingCartIcon,
  Star as StarIcon,
} from '@mui/icons-material';
import Checklist from '../components/Checklist';
import FAQAccordion from '../components/FAQAccordion';
import ComparisonTable from '../components/ComparisonTable';
import TwoColumnComparisonTable from '../components/TwoColumnComparisonTable';
import CenteredVideoPlayer from '../components/CenteredVideoPlayer';
import { ELEMENT_TYPES, LAYOUT_TYPES } from './offerElementTypes';
import { LAYOUT_CONFIGS } from './offerLayoutConfigs';
import { getListIcon } from './offerListIcons';
import { getPreset } from '../textStyle.js';

export const calculateVideoAspectRatio = (element) => {
  const width = element.styles?.width || '100%';
  const height = element.styles?.height || 225;

  // If width is 100% or auto, use a base width for calculation
  const baseWidth = (width === '100%' || width === 'auto') ? 400 : (typeof width === 'number' ? width : 400);

  // Parse height - handle both number and string (e.g., '315px', '225', 225)
  let heightNum;
  if (typeof height === 'number') {
    heightNum = height;
  } else if (typeof height === 'string') {
    // Remove 'px' or other units and parse
    heightNum = parseInt(height.replace(/px|rem|em|%/, '')) || 225;
  } else {
    heightNum = 225;
  }

  // Ensure height is valid
  if (!heightNum || heightNum <= 0) {
    heightNum = 225; // Default to 16:9 aspect ratio (400/225 ≈ 16/9)
  }

  // Calculate aspect ratio (width:height)
  const aspectRatio = baseWidth / heightNum;

  console.log('📐 calculateVideoAspectRatio:', {
    width,
    height,
    baseWidth,
    heightNum,
    aspectRatio,
    elementStyles: element.styles
  });

  // Convert to common aspect ratio strings
  // Round to nearest common ratio
  if (Math.abs(aspectRatio - 1) < 0.1) {
    return '1/1'; // Square
  } else if (Math.abs(aspectRatio - 16 / 9) < 0.1) {
    return '16/9'; // 16:9
  } else if (Math.abs(aspectRatio - 9 / 16) < 0.1) {
    return '9/16'; // 9:16 (portrait)
  } else if (Math.abs(aspectRatio - 4 / 5) < 0.1) {
    return '4/5'; // 4:5 (portrait)
  } else if (Math.abs(aspectRatio - 4 / 3) < 0.1) {
    return '4/3'; // 4:3
  } else if (Math.abs(aspectRatio - 3 / 4) < 0.1) {
    return '3/4'; // 3:4
  } else {
    // Return calculated ratio as string
    // Simplify the fraction
    const gcd = (a, b) => b === 0 ? a : gcd(b, a % b);
    const divisor = gcd(baseWidth, heightNum);
    const calculatedRatio = `${baseWidth / divisor}/${heightNum / divisor}`;
    console.log('📐 Using calculated ratio:', calculatedRatio);
    return calculatedRatio;
  }
};

// Render element for preview mode (mobile-optimized)
export const renderPreviewElement = (element, getZoneHeightStyle, forceRender) => {
  switch (element.type) {
    case ELEMENT_TYPES.HEADING:
      return (
        <Box
          sx={{
            ...element.styles,
            mb: 0.25,
            fontSize: { xs: '1.5rem', md: element.styles?.fontSize || '2rem' },
            padding: element.styles?.backgroundColor && element.styles?.backgroundColor !== 'transparent'
              ? element.styles?.padding || '8px 12px'
              : element.styles?.padding || 0,
            borderRadius: element.styles?.backgroundColor && element.styles?.backgroundColor !== 'transparent'
              ? '4px'
              : 0,
            display: element.styles?.backgroundColor && element.styles?.backgroundColor !== 'transparent'
              ? 'inline-block'
              : 'block',
            fontFamily: element.styles?.fontFamily || 'inherit',
            fontWeight: element.styles?.fontWeight || 'inherit',
            textAlign: element.styles?.textAlign || 'inherit',
            color: element.styles?.color || 'inherit',
            backgroundColor: element.styles?.backgroundColor || 'transparent',
            border: element.styles?.border || 'none',
            margin: element.styles?.margin || '0 0 2px 0'
          }}
          dangerouslySetInnerHTML={{ __html: element.content || 'Heading' }}
        />
      );

    case ELEMENT_TYPES.STAR_RATING:
      return (
        <Box
          sx={{
            width: '100%',
            display: 'flex',
            justifyContent: element.styles?.justifyContent || 'center',
            textAlign: element.styles?.textAlign || 'center',
            marginBottom: element.styles?.marginBottom || '10px',
          }}
        >
          <Box
            sx={{
              color: element.styles?.color || '#ffc107',
              fontSize: element.styles?.fontSize || '24px',
              ...element.styles,
              // Must come AFTER spread so they override element.styles.gap / display
              display: 'inline-flex',
              alignItems: (element.ratingTextPosition === 'top' || element.ratingTextPosition === 'bottom') ? 'flex-start' : 'center',
              flexDirection: (element.ratingTextPosition === 'top' || element.ratingTextPosition === 'bottom') ? 'column' : 'row',
              gap: element.ratingTextGap !== undefined ? element.ratingTextGap : '8px',
              justifyContent: undefined,
              textAlign: undefined,
              marginBottom: undefined,
            }}
          >
            {element.showRatingText && (!element.ratingTextPosition || element.ratingTextPosition === 'left' || element.ratingTextPosition === 'top') && (
              <Typography component="span" sx={{ fontWeight: 'bold', fontSize: 'inherit', color: 'inherit', lineHeight: 1, ...(element.ratingTextStyles || {}) }}>
                {element.ratingText !== undefined ? element.ratingText : `${element.rating || 5}/${element.maxStars || 5}`}
              </Typography>
            )}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: element.styles?.gap || '4px' }}>
              {Array.from({ length: element.maxStars || 5 }).map((_, idx) => (
                <Box key={idx} sx={{ color: idx < (element.rating || 5) ? element.styles?.color || '#ffc107' : '#e0e0e0', display: 'flex', alignItems: 'center' }}>
                  <StarIcon fontSize="inherit" />
                </Box>
              ))}
            </Box>
            {element.showRatingText && (element.ratingTextPosition === 'right' || element.ratingTextPosition === 'bottom') && (
              <Typography component="span" sx={{ fontWeight: 'bold', fontSize: 'inherit', color: 'inherit', lineHeight: 1, ...(element.ratingTextStyles || {}) }}>
                {element.ratingText !== undefined ? element.ratingText : `${element.rating || 5}/${element.maxStars || 5}`}
              </Typography>
            )}
          </Box>
        </Box>
      );
    case ELEMENT_TYPES.STAR_RATING_TABLE:
      return (
        <Box
          sx={{
            mb: element.styles?.marginBottom || '20px',
            backgroundColor: element.styles?.backgroundColor || '#ffffff',
            padding: element.styles?.padding || '24px',
            borderRadius: element.styles?.borderRadius || '12px',
            border: element.styles?.border || '1px solid #eeeeee',
            boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
            ...element.styles
          }}
        >
          {element.showTitle !== false && element.title && (
            <Typography
              variant="h6"
              sx={{
                color: element.styles?.titleColor || '#000000',
                fontSize: element.styles?.titleFontSize || '20px',
                fontWeight: 'bold',
                mb: 2.5,
                textAlign: 'left',
                lineHeight: 1.2
              }}
            >
              {element.title}
            </Typography>
          )}
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: element.styles?.justifyContent || 'flex-start' }}>
            {(element.rows || []).map((row, idx) => {
              const isSideBySide = element.rowLayout === 'side-by-side';
              const labelAlign = element.labelAlign || 'left';
              const starsAlign = element.starsAlign || (isSideBySide ? 'right' : 'left');

              return (
                <Box
                  key={row.id || idx}
                  sx={{
                    py: 1.5,
                    width: '100%',
                    display: 'flex',
                    flexDirection: isSideBySide ? 'row' : 'column',
                    alignItems: isSideBySide ? 'center' : 'stretch',
                    justifyContent: isSideBySide ? 'space-between' : 'flex-start',
                    borderBottom: element.showRowDividers && idx < (element.rows || []).length - 1 ? `${element.dividerThickness || '1px'} solid ${element.dividerColor || '#eeeeee'}` : 'none',
                    '&:last-child': { pb: 0 },
                    '&:first-of-type': { pt: 0 },
                    gap: isSideBySide ? 2 : 0
                  }}
                >
                  <Typography
                    variant="body1"
                    sx={{
                      fontWeight: element.styles?.fontWeight || '700',
                      fontFamily: element.styles?.fontFamily || 'inherit',
                      mb: isSideBySide ? 0 : 0.5,
                      color: element.styles?.color || '#1a1a1a',
                      fontSize: element.styles?.labelFontSize || '18px',
                      lineHeight: 1.4,
                      textAlign: labelAlign,
                      flex: isSideBySide ? 1 : 'none',
                      minWidth: isSideBySide ? '100px' : 'auto'
                    }}
                  >
                    {row.label}
                  </Typography>
                  <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    flex: isSideBySide ? 2 : 'none',
                    width: '100%',
                    justifyContent: isSideBySide ?
                      (starsAlign === 'left' ? 'flex-start' : starsAlign === 'center' ? 'center' : 'flex-end') :
                      (starsAlign === 'right' ? 'flex-end' : starsAlign === 'center' ? 'center' : 'flex-start')
                  }}>
                    <Box sx={{
                      display: 'flex',
                      flexDirection: 'row',
                      alignItems: 'center',
                      justifyContent: 'inherit',
                      width: '100%',
                      gap: 1.5
                    }}>
                      <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: element.styles?.gap || '4px',
                        flex: starsAlign === 'center' ? 'none' : 1,
                        justifyContent: starsAlign === 'center' ? 'center' : starsAlign === 'right' ? 'flex-end' : 'flex-start',
                        order: element.ratingTextPosition === 'left' ? 2 : 1
                      }}>
                        {Array.from({ length: element.maxStars || 5 }).map((_, i) => {
                          const isFull = i + 1 <= Math.floor(row.rating);
                          const isHalf = !isFull && i < row.rating;
                          return (
                            <StarIcon
                              key={i}
                              sx={{
                                fontSize: element.styles?.fontSize || '22px',
                                color: (isFull || isHalf) ? (element.styles?.starColor || element.styles?.color || '#ffc107') : '#e0e0e0',
                              }}
                            />
                          );
                        })}
                      </Box>

                      {row.ratingText && (
                        <Box sx={{
                          flex: element.ratingTextAlign === 'center' ? 'none' : 1,
                          textAlign: element.ratingTextAlign || 'left',
                          order: element.ratingTextPosition === 'left' ? 1 : 2
                        }}>
                          <Typography
                            variant="body1"
                            sx={{
                              fontWeight: element.styles?.fontWeight || '800',
                              fontFamily: element.styles?.fontFamily || 'inherit',
                              color: element.styles?.color || '#1a1a1a',
                              fontSize: element.styles?.labelFontSize || '18px'
                            }}
                          >
                            {row.ratingText}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </Box>
                </Box>
              );
            })}
          </Box>
        </Box>
      );

    case ELEMENT_TYPES.TEXT:
      return (
        <Box
          sx={{
            ...element.styles,
            mb: 0, // Remove bottom margin for inline layout
            whiteSpace: 'pre-wrap',
            fontSize: { xs: '0.875rem', md: element.styles?.fontSize || '1rem' },
            padding: element.styles?.backgroundColor && element.styles?.backgroundColor !== 'transparent'
              ? element.styles?.padding || '8px 12px'
              : element.styles?.padding || 0,
            borderRadius: element.styles?.backgroundColor && element.styles?.backgroundColor !== 'transparent'
              ? '4px'
              : 0,
            display: element.styles?.backgroundColor && element.styles?.backgroundColor !== 'transparent'
              ? 'block'
              : 'block', // Use block for full width
            fontFamily: element.styles?.fontFamily || 'inherit',
            fontWeight: element.styles?.fontWeight || 'inherit',
            textAlign: element.styles?.textAlign || 'inherit',
            color: element.styles?.color || 'inherit',
            backgroundColor: element.styles?.backgroundColor || 'transparent',
            border: element.styles?.border || 'none',
            margin: element.styles?.margin || '0',
            width: '100%', // Take full width of the zone
            wordWrap: 'break-word'
          }}
          dangerouslySetInnerHTML={{ __html: element.content || 'Text content' }}
        />
      );

    case ELEMENT_TYPES.QUOTATION_BLOCK:
      return (
        <Box
          sx={{
            position: 'relative',
            backgroundColor: element.styles?.backgroundColor || '#fce7f3',
            color: element.styles?.color || '#101022',
            borderRadius: element.styles?.borderRadius || '16px',
            padding: element.styles?.padding || '24px 32px 24px 32px',
            mb: 1,
            fontStyle: 'italic',
            minHeight: '120px',
            overflow: 'visible',
            fontSize: element.styles?.fontSize || { xs: '16px', md: '18px' },
            fontWeight: element.styles?.fontWeight || 400,
            lineHeight: element.styles?.lineHeight || 1.5,
            fontFamily: element.styles?.fontFamily || "'Inter', sans-serif",
            ...element.styles
          }}
        >
          {/* Quotation Icon Overlay */}
          <Box
            sx={{
              position: 'absolute',
              top: '-12px',
              left: '12px',
              width: { xs: 36, md: 40 },
              height: { xs: 36, md: 40 },
              background: '#fff',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
              zIndex: 2,
            }}
          >
            <Typography
              sx={{
                fontSize: { xs: '20px', md: '24px' },
                fontFamily: 'serif',
                fontWeight: 'bold',
                color: '#101022',
                lineHeight: 1
              }}
            >
              &ldquo;
            </Typography>
          </Box>
          {/* Text Content */}
          <Box
            sx={{
              fontStyle: 'italic',
              fontSize: element.styles?.fontSize || { xs: '16px', md: '18px' },
              fontWeight: element.styles?.fontWeight || 400,
              lineHeight: element.styles?.lineHeight || 1.5,
              fontFamily: element.styles?.fontFamily || "'Inter', sans-serif",
              display: 'block'
            }}
            dangerouslySetInnerHTML={{ __html: element.content || 'Quote text here...' }}
          />
        </Box>
      );

    case ELEMENT_TYPES.IMAGE:
      return element.content ? (
        <Box sx={{
          textAlign: 'center',
          mb: 1,
          backgroundColor: element.styles?.backgroundColor || 'transparent',
          padding: element.styles?.padding || '0',
          margin: element.styles?.margin || '0 0 8px 0',
          border: element.styles?.border || 'none',
          borderRadius: element.styles?.borderRadius || '0'
        }}>
          <img
            src={element.content}
            alt={element.alt || ''}
            style={{
              maxWidth: '100%',
              borderRadius: element.styles?.borderRadius || '8px',
              border: element.styles?.border || 'none',
              width: element.styles?.width || 'auto',
              height: element.styles?.height || 'auto',
              objectFit: element.styles?.objectFit || 'cover'
            }}
          />
        </Box>
      ) : (
        <Box sx={{
          textAlign: 'center',
          p: { xs: 2, md: 4 },
          backgroundColor: '#f0f0f0',
          borderRadius: 1,
          mb: 1
        }}>
          <Typography variant="body2" color="text.secondary">
            📷 Image placeholder
          </Typography>
        </Box>
      );

    case ELEMENT_TYPES.IMAGE_OVERLAY: {
      const previewWidth = typeof element.styles?.width === 'number' ? `${element.styles.width}px` : (element.styles?.width || '100%');
      const previewHeight = typeof element.styles?.height === 'number' ? `${element.styles.height}px` : (element.styles?.height || 'auto');

      return (
        <Box
          sx={{
            ...element.styles,
            position: 'relative',
            width: previewWidth,
            height: previewHeight,
            maxWidth: '100%',
            borderRadius: element.styles?.borderRadius || '8px',
            overflow: 'hidden',
            mb: element.styles?.marginBottom || 2,
            margin: '0 auto',
            lineHeight: 0
          }}
        >
          <img
            src={element.imageSrc || element.content?.image || 'https://via.placeholder.com/800x400?text=Background+Image'}
            alt="Background"
            style={{
              position: 'absolute',
              left: '50%',
              top: 0,
              transform: `translate(-50%, ${element.imagePosition?.y || 0}px) scale(${element.imageScale || 1})`,
              transformOrigin: 'top center',
              maxWidth: 'none',
              maxHeight: 'none',
              display: 'block'
            }}
          />
          {element.zones.map((zone) => (
            <Box
              key={zone.id}
              sx={{
                ...element.overlayStyles,
                position: 'absolute',
                zIndex: 2,
              }}
            >
              {zone.elements.map((zoneElement, idx) => (
                <Box key={zoneElement.id || idx}>
                  {renderPreviewElement(zoneElement, getZoneHeightStyle, forceRender)}
                </Box>
              ))}
            </Box>
          ))}
        </Box>
      );
    }

    case ELEMENT_TYPES.CROPPABLE_IMAGE:
      const croppableWidth = element.styles?.width || 300;
      const croppableHeight = element.styles?.height || 200;
      return element.croppedImage ? (
        <Box sx={{
          textAlign: 'center',
          mb: 0,
          backgroundColor: element.styles?.backgroundColor || 'transparent',
          padding: element.styles?.padding || '0',
          margin: element.styles?.margin || '0',
          border: element.styles?.border || 'none',
          borderRadius: element.styles?.borderRadius || '0',
          display: 'inline-block'
        }}>
          <img
            src={element.croppedImage}
            alt="Croppable Image"
            style={{
              width: `${croppableWidth}px`,
              height: `${croppableHeight}px`,
              objectFit: 'cover',
              border: element.styles?.border || 'none',
              borderRadius: element.styles?.borderRadius || '8px',
              display: 'block',
              margin: '0'
            }}
          />
        </Box>
      ) : (
        <Box sx={{
          textAlign: 'center',
          p: { xs: 2, md: 4 },
          backgroundColor: '#f0f0f0',
          borderRadius: '8px',
          width: `${croppableWidth}px`,
          height: `${croppableHeight}px`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          margin: '0',
          mb: 0
        }}>
          <Typography variant="body2" color="text.secondary">
            📷 Croppable Image
          </Typography>
        </Box>
      );

    case ELEMENT_TYPES.CIRCULAR_IMAGE:
      const circularSize = element.styles?.width || 200;
      return element.croppedImage ? (
        <Box sx={{
          textAlign: 'center',
          mb: 0, // Remove bottom margin for inline layout
          backgroundColor: element.styles?.backgroundColor || 'transparent',
          padding: element.styles?.padding || '0',
          margin: element.styles?.margin || '0',
          border: element.styles?.border || 'none',
          borderRadius: element.styles?.borderRadius || '0',
          display: 'inline-block'
        }}>
          <img
            src={element.croppedImage}
            alt="Circular Image"
            style={{
              width: `${circularSize}px`,
              height: `${circularSize}px`,
              borderRadius: '50%',
              objectFit: 'cover',
              border: element.styles?.border || 'none',
              display: 'block',
              margin: '0'
            }}
          />
        </Box>
      ) : (
        <Box sx={{
          textAlign: 'center',
          p: { xs: 2, md: 4 },
          backgroundColor: '#f0f0f0',
          borderRadius: '50%',
          width: `${circularSize}px`,
          height: `${circularSize}px`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          margin: '0',
          mb: 0 // Remove bottom margin for inline layout
        }}>
          <Typography variant="body2" color="text.secondary">
            📷 Circular Image
          </Typography>
        </Box>
      );

    case ELEMENT_TYPES.FITTABLE_IMAGE: {
      // Handle responsive frame dimensions
      const getFrameWidth = (styleWidth) => {
        if (styleWidth === "100%" || styleWidth === "auto") {
          return "100%";
        }
        if (typeof styleWidth === 'number') return `${styleWidth}px`;
        if (typeof styleWidth === 'string') {
          const numValue = parseInt(styleWidth, 10);
          return isNaN(numValue) ? "400px" : `${numValue}px`;
        }
        return "400px";
      };

      const getFrameHeight = (styleHeight) => {
        if (styleHeight === "auto") return "300px";
        if (typeof styleHeight === 'number') return `${styleHeight}px`;
        if (typeof styleHeight === 'string') {
          const numValue = parseInt(styleHeight, 10);
          return isNaN(numValue) ? "300px" : `${numValue}px`;
        }
        return "300px";
      };

      const fittableFrameWidth = getFrameWidth(element.styles?.width);
      const fittableFrameHeight = getFrameHeight(element.styles?.height);
      const isFullWidth = element.styles?.width === "100%";
      const fittablePosition = element.imagePosition || { x: 0, y: 0 };
      const fittableScale = element.imageScale || 1;

      // Use imageSrc or fallback to content property
      const imageSource = element.imageSrc || element.content;



      return imageSource ? (
        <Box sx={{
          ...element.styles,
          width: isFullWidth ? '100%' : 'auto',
          textAlign: isFullWidth ? 'left' : 'center',
          mb: 0,
          backgroundColor: element.styles?.backgroundColor || 'transparent',
          padding: element.styles?.padding || '0',
          margin: element.styles?.margin || '0',
          border: element.styles?.border || 'none',
          borderRadius: element.styles?.borderRadius || '0',
          display: isFullWidth ? 'block' : 'inline-block',
          height: 'auto' // Container stretches to child
        }}>
          <Box sx={{
            position: 'relative',
            width: fittableFrameWidth,
            height: fittableFrameHeight,
            border: 'none',
            borderRadius: '8px',
            overflow: 'hidden',
            backgroundColor: 'transparent',
            margin: isFullWidth ? '0' : 'auto'
          }}>
            <img
              src={imageSource}
              alt="Fittable Image"
              style={{
                position: 'absolute',
                left: '50%',
                top: 0,
                transform: `translate(-50%, ${fittablePosition.y}px) scale(${fittableScale})`,
                transformOrigin: 'top center',
                maxWidth: 'none',
                maxHeight: 'none'
              }}

            />
          </Box>
        </Box>
      ) : (
        <Box sx={{
          ...element.styles,
          textAlign: 'center',
          p: { xs: 2, md: 4 },
          backgroundColor: '#f0f0f0',
          border: 'none',
          borderRadius: '8px',
          width: fittableFrameWidth,
          height: fittableFrameHeight,
          display: 'flex',
          alignItems: 'flex-start', // Match top alignment
          pt: 4, // Add some top padding for the icon/label
          justifyContent: 'center',
          margin: '0',
          mx: isFullWidth ? 0 : 'auto'
        }}>
          <Typography variant="body2" color="text.secondary">
            📷 Click to upload image
          </Typography>
        </Box>
      );
    }

    case ELEMENT_TYPES.LEFT_IMAGE_TEXT:
      const previewAlignment = element.imageAlignment === 'top' ? 'flex-start' :
        element.imageAlignment === 'bottom' ? 'flex-end' : 'center';
      return (
        <Box sx={{
          ...element.styles,
          display: 'flex',
          alignItems: previewAlignment,
          gap: `${element.spacing || 20}px`,
          mb: 1,
          flexWrap: 'wrap'
        }}>
          {/* Circular Image */}
          <Box sx={{
            flexShrink: 0,
            width: `${element.imageSize || 120}px`,
            height: `${element.imageSize || 120}px`
          }}>
            <img
              src={element.content.image}
              alt="Profile image"
              style={{
                width: `${element.imageSize || 120}px`,
                height: `${element.imageSize || 120}px`,
                objectFit: 'cover',
                borderRadius: '50%',
                border: '3px solid #e0e0e0',
                display: 'block'
              }}
              onError={(e) => {
                if (e.target.src !== 'https://via.placeholder.com/150x150?text=Profile') {
                  e.target.src = 'https://via.placeholder.com/150x150?text=Profile';
                }
              }}
            />
          </Box>

          {/* Text Content */}
          <Box sx={{ flex: 1, minHeight: '56px', display: 'flex', alignItems: 'center' }}>
            <Typography variant="body1" sx={{
              color: element.textStyles?.color || 'inherit',
              lineHeight: element.textStyles?.lineHeight || 1.6,
              fontSize: element.textStyles?.fontSize || '16px',
              fontWeight: element.textStyles?.fontWeight || 'normal',
              fontFamily: element.textStyles?.fontFamily || 'inherit',
              textAlign: element.textStyles?.textAlign || 'left',
              backgroundColor: element.textStyles?.backgroundColor || 'transparent',
              padding: element.textStyles?.padding || '0px',
              borderRadius: element.textStyles?.borderRadius || '0px'
            }}>
              {element.content.text}
            </Typography>
          </Box>
        </Box>
      );

    case ELEMENT_TYPES.BUTTON:
      const previewButtonKey = `preview-btn-${element.id}-${JSON.stringify(element.styles)}-${forceRender}`;
      return (
        <Box key={previewButtonKey} sx={{
          textAlign: element.styles?.textAlign || 'center',
          mb: 1,
          backgroundColor: element.styles?.containerBackgroundColor || 'transparent',
          padding: element.styles?.containerPadding || '0',
          margin: element.styles?.containerMargin || '0 0 8px 0',
          border: element.styles?.containerBorder || 'none',
          borderRadius: element.styles?.containerBorderRadius || '0'
        }}>
          <button
            style={{
              backgroundColor: element.styles?.backgroundColor || '#ff6b35',
              color: element.styles?.color || '#ffffff',
              fontWeight: element.styles?.fontWeight || 400,
              fontFamily: element.styles?.fontFamily || 'inherit',
              borderRadius: element.styles?.borderRadius || '4px',
              fontSize: element.styles?.fontSize || '18px',
              padding: element.styles?.padding || '12px 32px',
              width: element.styles?.width || 'auto',
              minHeight: '44px',
              border: element.styles?.border || 'none',
              textTransform: element.styles?.textTransform || 'none',
              fontStyle: element.styles?.fontStyle || 'normal',
              transform: element.styles?.transform || 'none',
              display: element.styles?.display || 'inline-block',
              textDecoration: element.styles?.textDecoration || 'none',
              cursor: element.styles?.cursor || 'pointer',
              transition: element.styles?.transition || 'all 0.2s ease',
              boxShadow: element.styles?.boxShadow || '0 2px 4px rgba(0,0,0,0.1)',
              textAlign: element.styles?.textAlign || 'center',
              margin: element.styles?.margin || '0',
              background: element.styles?.background || element.styles?.backgroundColor || '#ff6b35',
              alignItems: element.styles?.alignItems || undefined,
              justifyContent: element.styles?.justifyContent || undefined,
              gap: element.styles?.gap || undefined
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.opacity = '0.8';
              const baseTransform = element.styles?.transform || 'none';
              e.currentTarget.style.transform = baseTransform === 'none' ? 'translateY(-1px)' : `${baseTransform} translateY(-1px)`;
              if (!element.styles?.boxShadow) {
                e.currentTarget.style.boxShadow = '0 4px 8px rgba(0,0,0,0.15)';
              }
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.opacity = '1';
              e.currentTarget.style.transform = element.styles?.transform || 'none';
              e.currentTarget.style.boxShadow = element.styles?.boxShadow || '0 2px 4px rgba(0,0,0,0.1)';
            }}
            onClick={() => {
              if (element.url && element.url !== '#') {
                window.open(element.url, '_blank');
              }
            }}
          >
            {element.iconLeft === 'cart' && <ShoppingCartIcon sx={{ color: '#d4a000', fontSize: '2.2rem', mr: 1 }} />}
            <span style={{ fontWeight: element.styles?.fontWeight || 'bold', fontSize: element.styles?.fontSize || '2rem', color: element.styles?.color || '#222' }}>
              {element.styles?.textTransform === 'titlecase' && element.content
                ? element.content.toLowerCase().replace(/\b\w/g, l => l.toUpperCase())
                : element.content || 'Button'}
            </span>
            {element.iconRight === 'arrow' && <ArrowForwardIcon sx={{ color: '#222', fontSize: '2.2rem', ml: 1 }} />}
          </button>
        </Box>
      );

    case ELEMENT_TYPES.VIDEO_WITH_BUTTON_OVERLAY: {
      const previewAspectRatio = calculateVideoAspectRatio(element);
      const [pw, ph] = previewAspectRatio.split('/').map(Number);
      const isPreviewVertical = pw && ph && ph > pw;

      let dynamicPreviewBottom = '24px';
      if (isPreviewVertical) {
        const ratioValue = ph / pw;
        if (ratioValue > 2) {
          dynamicPreviewBottom = '100px';
        } else if (ratioValue > 1.5) {
          dynamicPreviewBottom = '60px';
        } else if (ratioValue > 1) {
          dynamicPreviewBottom = '48px';
        }
      }

      const align = element.styles?.textAlign || 'center';

      return (
        <Box
          sx={{
            position: 'relative',
            mb: 2,
            width: 'calc(100% + 32px)',
            maxWidth: 'calc(100% + 32px)',
            mx: -2,
            aspectRatio: previewAspectRatio,
            backgroundColor: '#000',
            borderRadius: 0,
            overflow: 'hidden'
          }}
        >
          {element.videoFile ? (
            <CenteredVideoPlayer
              src={element.videoFile}
              poster={element.thumbnailUrl || undefined}
              fillParent={true}
              showControlsBar={false}
              style={{
                width: '100%',
                height: '100%',
                maxWidth: '100%',
                margin: 0,
                padding: 0,
                objectFit: isPreviewVertical ? 'cover' : 'contain'
              }}
              videoProps={{ title: 'Uploaded Video' }}
            />
          ) : (
            <Box sx={{
              width: '100%',
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1
            }}>
              <Typography variant="body2" color="text.secondary">
                🎥 No video uploaded
              </Typography>
            </Box>
          )}

          {/* Centered button at the bottom */}
          <Box
            sx={{
              position: 'absolute',
              bottom: dynamicPreviewBottom,
              left: align === 'center' ? '50%' : (align === 'right' ? 'auto' : '32px'),
              right: align === 'right' ? '32px' : 'auto',
              transform: align === 'center' ? 'translateX(-50%)' : 'none',
              width: '55%',
              zIndex: 20,
              display: 'flex',
              justifyContent: align === 'center' ? 'center' : (align === 'right' ? 'flex-end' : 'flex-start')
            }}
          >
            <button
              style={{
                ...(element.styles || {}),
                width: '100%',
                height: '44px',
                minHeight: '44px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                textAlign: 'center',
                border: element.styles?.border || 'none'
              }}
            >
              {element.content || 'Click Here'}
            </button>
          </Box>
        </Box>
      );
    }

    case ELEMENT_TYPES.VIDEO:
      console.log('🎥 renderPreviewElement VIDEO called for element:', element);

      console.log('🎥 element.videoFile:', element.videoFile);
      console.log('🎥 element.content:', element.content);

      // Calculate aspect ratio from element styles
      const previewAspectRatio = calculateVideoAspectRatio(element);

      console.log('🎥 Preview aspect ratio calculation:', {
        elementStyles: element.styles,
        calculatedAspectRatio: previewAspectRatio,
        width: element.styles?.width,
        height: element.styles?.height
      });

      if (element.videoFile) {
        // Show uploaded video with calculated aspect ratio
        // For brands page, always use full width regardless of preset
        console.log('🎥 Rendering uploaded video:', element.videoFile, 'with aspect ratio:', previewAspectRatio);

        // Remove height/width from element.styles to let the container's aspectRatio handle sizing
        // This prevents conflicting styles from overriding the aspect ratio
        const { height, width, maxWidth, ...otherStyles } = element.styles || {};
        const videoStyles = {
          width: '100%',
          maxWidth: '100%',
          height: '100%',
          border: 'none',
          borderRadius: 0,
          margin: 0,
          padding: 0,
          objectFit: 'contain', // Ensure video fits within container while maintaining aspect ratio
          ...otherStyles
        };

        return (
          <Box sx={{
            textAlign: 'center',
            mb: 2,
            width: 'calc(100% + 32px)',
            maxWidth: 'calc(100% + 32px)',
            aspectRatio: previewAspectRatio,
            mx: -2, // Negative margin to counteract container padding (16px = 2 * 8px)
            px: 0,
            backgroundColor: '#000', // Black background for video container
            borderRadius: 0,
            overflow: 'hidden'
          }}>
            <CenteredVideoPlayer
              src={element.videoFile}
              poster={element.thumbnailUrl || undefined}
              style={videoStyles}
              videoProps={{ title: 'Uploaded Video' }}
            />
          </Box>
        );
      } else {
        // Show placeholder with calculated aspect ratio
        return (
          <Box sx={{
            textAlign: 'center',
            backgroundColor: '#000',
            borderRadius: 0,
            mb: 2,
            width: 'calc(100% + 32px)',
            maxWidth: 'calc(100% + 32px)',
            mx: -2,
            aspectRatio: previewAspectRatio,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <Typography variant="body2" color="text.secondary">
              🎥 No video uploaded
            </Typography>
          </Box>
        );
      }

    case ELEMENT_TYPES.MEDIA:
      if (element.mediaUrl) {
        // Check if it's a video or image
        const isMediaVideo = element.mediaUrl.match(/\.(mp4|avi|mov|wmv|flv|webm)$/i);
        const mediaAspectRatio = element.styles?.aspectRatio || 'auto';

        if (isMediaVideo) {
          // Render video with aspect ratio
          return (
            <Box sx={{
              textAlign: 'center',
              mb: 2,
              width: '100%',
              maxWidth: '100%',
              aspectRatio: mediaAspectRatio,
              overflow: 'hidden',
              borderRadius: '8px'
            }}>
              <video
                src={element.mediaUrl}
                controls
                poster={element.thumbnailUrl || undefined}
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover'
                }}
              />
            </Box>
          );
        } else {
          // Render image
          return (
            <Box sx={{
              textAlign: 'center',
              mb: 2,
              width: '100%',
              maxWidth: '100%'
            }}>
              <img
                src={element.mediaUrl}
                alt="Media"
                style={{
                  maxWidth: '100%',
                  height: 'auto',
                  borderRadius: '8px'
                }}
              />
            </Box>
          );
        }
      } else {
        // Show placeholder
        return (
          <Box sx={{
            textAlign: 'center',
            backgroundColor: '#f5f5f5',
            borderRadius: 1,
            mb: 2,
            p: 3,
            border: '2px dashed #ccc'
          }}>
            <Typography variant="body2" color="text.secondary">
              📁 No media uploaded. Click to upload image or video.
            </Typography>
          </Box>
        );
      }

    case ELEMENT_TYPES.HTML:
      return element.content ? (
        <Box sx={{ mb: 2 }}>
          <div dangerouslySetInnerHTML={{ __html: element.content }} />
        </Box>
      ) : (
        <Box sx={{
          p: { xs: 1, md: 2 },
          backgroundColor: '#f5f5f5',
          borderRadius: 1,
          mb: 2
        }}>
          <Typography variant="body2" color="text.secondary">
            💻 HTML content will appear here
          </Typography>
        </Box>
      );

    case ELEMENT_TYPES.DIVIDER:
      return (
        <Box sx={{
          height: '1px',
          backgroundColor: '#e0e0e0',
          margin: { xs: '20px 0', md: '30px 0' },
          ...element.styles
        }} />
      );

    case ELEMENT_TYPES.LAYOUT:
      const config = LAYOUT_CONFIGS[element.layoutType];

      // Special handling for INLINE_ROW layout - use same logic as main renderElement
      if (element.layoutType === LAYOUT_TYPES.INLINE_ROW) {
        return (
          <Box
            sx={{
              display: 'flex',
              gap: 2,
              mb: 3,
              flexDirection: 'row',
              flexWrap: 'wrap',
              alignItems: 'center',
              backgroundColor: element.styles?.backgroundColor || 'transparent',
              padding: element.styles?.padding || '16px',
              margin: element.styles?.margin || '0 0 24px 0',
              border: element.styles?.border || 'none',
              borderRadius: element.styles?.borderRadius || '0',
              textAlign: element.styles?.textAlign || 'left',
              fontFamily: element.styles?.fontFamily || 'inherit',
              fontSize: element.styles?.fontSize || 'inherit',
              color: element.styles?.color || 'inherit',
              ...element.styles
            }}
          >
            {element.zones[0].elements.map((zoneElement, index) => (
              <Box key={zoneElement.id} sx={{
                display: 'inline-block',
                margin: '0 5px',
                verticalAlign: 'top',
                flexShrink: 0
              }}>
                {renderPreviewElement(zoneElement, getZoneHeightStyle, forceRender)}
              </Box>
            ))}
          </Box>
        );
      }

      return (
        <Box
          sx={{
            display: 'flex',
            gap: { xs: 1, md: 2 },
            mb: 3,
            flexDirection: { xs: 'column', md: element.layoutType === LAYOUT_TYPES.THREE_COLUMN ? 'wrap' : 'row' },
            backgroundColor: element.styles?.backgroundColor || 'transparent',
            padding: element.styles?.padding || '16px',
            margin: element.styles?.margin || '0 0 24px 0',
            border: element.styles?.border || 'none',
            borderRadius: element.styles?.borderRadius || '0',
            textAlign: element.styles?.textAlign || 'left',
            fontFamily: element.styles?.fontFamily || 'inherit',
            fontSize: element.styles?.fontSize || 'inherit',
            color: element.styles?.color || 'inherit',
            ...element.styles
          }}
        >
          {element.zones.map((zone, index) => {
            const zoneConfig = config.zones[index];
            return (
              <Box
                key={zone.id}
                data-zone-id={zone.id}
                sx={{
                  width: { xs: '100%', md: zoneConfig.width },
                  textAlign: zoneConfig.textAlign || 'left',
                  p: { xs: 0.5, md: 1 },
                  border: '1px dashed #e0e0e0', // Default border
                  borderRadius: 1,
                  overflow: 'visible', // Let content fit height without scrolling
                  ...getZoneHeightStyle(zone.id, zoneConfig.height || 'auto') // Apply after so it can override border
                }}
              >

                {zone.elements.length === 0 ? (
                  <Box sx={{
                    minHeight: '60px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'text.secondary',
                    fontStyle: 'italic'
                  }}>
                    <Typography variant="caption">
                      Empty zone
                    </Typography>
                  </Box>
                ) : (
                  zone.elements.map((zoneElement) => (
                    <Box key={`${zoneElement.id}-${forceRender}`}>
                      {renderPreviewElement(zoneElement, getZoneHeightStyle, forceRender)}
                    </Box>
                  ))
                )}
              </Box>
            );
          })}
        </Box>
      );

    case ELEMENT_TYPES.CHECKLIST:
      return <Checklist title={element.title} items={element.items} />;

    case ELEMENT_TYPES.COMPARISON_TABLE:

      return (
        <ComparisonTable
          key={`preview-${element.id}-${element.productAImage || 'noA'}-${element.productBImage || 'noB'}`}
          title={element.title}
          productA={element.productA}
          productB={element.productB}
          productAImage={element.productAImage}
          productBImage={element.productBImage}
          rows={element.rows}
          titleStylePreset={element.titleStylePreset}
          productNameStylePreset={element.productNameStylePreset}
          featureTitleStylePreset={element.featureTitleStylePreset}
          featureDescStylePreset={element.featureDescStylePreset}
        />
      );

    case ELEMENT_TYPES.TWO_COLUMN_COMPARISON:
      return (
        <TwoColumnComparisonTable
          key={`preview-${element.id}-${element.leftColumnImage || 'noLeft'}-${element.rightColumnImage || 'noRight'}`}
          leftColumnTitle={element.leftColumnTitle}
          rightColumnTitle={element.rightColumnTitle}
          leftColumnImage={element.leftColumnImage}
          rightColumnImage={element.rightColumnImage}
          leftItems={element.leftItems}
          rightItems={element.rightItems}
          titleStylePreset={element.titleStylePreset}
          itemStylePreset={element.itemStylePreset}
        />
      );

    case ELEMENT_TYPES.BUTTON_WITH_NOTICE:
      const getPreviewVariantStyles = (variant, styleVariant = 'default') => {
        const styleVariants = {
          'urgent-sale': {
            'default': {
              backgroundColor: '#ff69b4', // pink
              noticeColor: '#101022', // black text on pink
              buttonBg: '#101022', // black button
              buttonColor: '#ffffff', // white text on black
              border: 'none',
              borderRadius: '8px'
            },
            'modern': {
              backgroundColor: '#ffffff', // white
              noticeColor: '#101022', // black text on white (changed from orange/yellow)
              buttonBg: '#ff69b4', // pink button
              buttonColor: '#101022', // black text on pink
              border: '2px solid #ff69b4',
              borderRadius: '12px'
            },
            'minimal': {
              backgroundColor: '#fafafa', // light gray (close to white)
              noticeColor: '#101022', // black text (changed from orange/yellow)
              buttonBg: '#ff69b4', // pink button
              buttonColor: '#101022', // black text on pink
              border: '1px solid #e0e0e0',
              borderRadius: '4px'
            },
            'gradient': {
              backgroundColor: 'linear-gradient(135deg, #ff69b4, #ff1493)', // pink gradient
              noticeColor: '#ffffff', // white text on pink gradient
              buttonBg: '#ffffff', // white button
              buttonColor: '#101022', // black text on white
              border: 'none',
              borderRadius: '16px'
            }
          },
          'membership-quiz': {
            'default': {
              backgroundColor: '#0000ff', // blue
              noticeColor: '#ffffff', // white text on blue
              buttonBg: '#ffffff', // white button
              buttonColor: '#0000ff', // blue text on white
              border: 'none',
              borderRadius: '8px'
            },
            'elegant': {
              backgroundColor: '#000080', // dark blue
              noticeColor: '#ffffff', // white text on dark blue
              buttonBg: '#ffffff', // white button
              buttonColor: '#000080', // dark blue text on white
              border: '2px solid #4169e1',
              borderRadius: '12px'
            },
            'soft': {
              backgroundColor: '#e6f3ff', // light blue
              noticeColor: '#0000ff', // blue text on light blue
              buttonBg: '#0000ff', // blue button
              buttonColor: '#ffffff', // white text on blue
              border: '1px solid #b3d9ff',
              borderRadius: '8px'
            },
            'corporate': {
              backgroundColor: '#ffffff', // white
              noticeColor: '#101022', // black text on white
              buttonBg: '#0000ff', // blue button
              buttonColor: '#ffffff', // white text on blue
              border: '2px solid #e0e0e0',
              borderRadius: '6px'
            }
          },
          'media-buzz': {
            'default': {
              backgroundColor: '#ffffff', // white
              noticeColor: '#101022', // black text on white
              buttonBg: '#ffff00', // yellow button
              buttonColor: '#101022', // black text on yellow
              border: '2px dashed #101022',
              borderRadius: '8px'
            },
            'news': {
              backgroundColor: '#f8f9fa', // light gray (close to white)
              noticeColor: '#101022', // black text
              buttonBg: '#0000ff', // blue button
              buttonColor: '#ffffff', // white text on blue
              border: '1px solid #e0e0e0',
              borderRadius: '8px'
            },
            'viral': {
              backgroundColor: '#ffff00', // yellow
              noticeColor: '#101022', // black text on yellow
              buttonBg: '#101022', // black button
              buttonColor: '#ffffff', // white text on black
              border: '2px solid #ffd700',
              borderRadius: '12px'
            },
            'trending': {
              backgroundColor: '#e6f3ff', // light blue
              noticeColor: '#0000ff', // blue text on light blue
              buttonBg: '#0000ff', // blue button
              buttonColor: '#ffffff', // white text on blue
              border: '1px solid #b3d9ff',
              borderRadius: '10px'
            }
          }
        };

        return styleVariants[variant]?.[styleVariant] || styleVariants[variant]?.['default'] || {
          backgroundColor: '#ff69b4',
          noticeColor: '#101022',
          buttonBg: '#101022',
          buttonColor: '#ffffff',
          border: 'none',
          borderRadius: '8px'
        };
      };

      const previewVariantStyles = getPreviewVariantStyles(element.variant, element.styleVariant);

      // Get custom styles from sub-element styling
      const noticeStyles = element.subElementStyles?.notice || {};
      const callToActionStyles = element.subElementStyles?.callToAction || {};
      const buttonStyles = element.subElementStyles?.button || {};
      const containerStyles = element.styles || {};

      return (
        <Box
          sx={{
            backgroundColor: element.styles?.backgroundColor || previewVariantStyles.backgroundColor,
            background: element.styles?.background || (previewVariantStyles.backgroundColor.includes('gradient')
              ? previewVariantStyles.backgroundColor
              : previewVariantStyles.backgroundColor),
            padding: element.styles?.padding || '20px',
            borderRadius: element.styles?.borderRadius || previewVariantStyles.borderRadius,
            textAlign: element.styles?.textAlign || 'center',
            marginBottom: '16px',
            border: element.styles?.border || previewVariantStyles.border,
            boxShadow: element.styleVariant === 'gradient' ? '0 8px 32px rgba(255, 107, 107, 0.3)' : '0 2px 8px rgba(0,0,0,0.1)',
            margin: element.styles?.margin || '0',
            color: element.styles?.color || previewVariantStyles.noticeColor,
            fontFamily: element.styles?.fontFamily || 'inherit',
            fontSize: element.styles?.fontSize || 'inherit',
            fontWeight: element.styles?.fontWeight || 'inherit',
            ...element.styles
          }}
        >
          {/* Notice Text */}
          <Typography
            variant="body1"
            sx={{
              color: noticeStyles.color || previewVariantStyles.noticeColor,
              fontWeight: noticeStyles.fontWeight || 'bold',
              fontSize: noticeStyles.fontSize || 'inherit',
              fontFamily: noticeStyles.fontFamily || 'inherit',
              textAlign: noticeStyles.textAlign || 'inherit',
              backgroundColor: noticeStyles.backgroundColor || 'transparent',
              padding: noticeStyles.padding || '0',
              margin: noticeStyles.margin || '0 0 16px 0',
              border: noticeStyles.border || 'none',
              borderRadius: noticeStyles.borderRadius || '0',
              marginBottom: '16px',
              lineHeight: 1.5
            }}
          >
            {element.content?.notice?.split('\n').map((line, index) => (
              <span key={index}>
                {line.includes('70%') ? (
                  <span style={{
                    color: noticeStyles.color || previewVariantStyles.noticeColor,
                    fontWeight: noticeStyles.fontWeight || 'bold'
                  }}>{line}</span>
                ) : line.includes('exclusive link below') ? (
                  <span>
                    {line.replace('exclusive link below', '')}
                    <span style={{ color: '#1976d2', textDecoration: 'underline' }}>exclusive link below</span>
                  </span>
                ) : (
                  line
                )}
                {index < element.content?.notice?.split('\n').length - 1 && <br />}
              </span>
            ))}
          </Typography>

          {/* Call to Action Text */}
          <Typography
            variant="body1"
            sx={{
              color: callToActionStyles.color || previewVariantStyles.noticeColor,
              fontWeight: callToActionStyles.fontWeight || 'inherit',
              fontSize: callToActionStyles.fontSize || 'inherit',
              fontFamily: callToActionStyles.fontFamily || 'inherit',
              textAlign: callToActionStyles.textAlign || 'inherit',
              backgroundColor: callToActionStyles.backgroundColor || 'transparent',
              padding: callToActionStyles.padding || '0',
              margin: callToActionStyles.margin || '0 0 16px 0',
              border: callToActionStyles.border || 'none',
              borderRadius: callToActionStyles.borderRadius || '0',
              marginBottom: '16px'
            }}
          >
            {element.content?.callToAction}
          </Typography>

          {/* Button */}
          <button
            style={{
              backgroundColor: buttonStyles.backgroundColor || previewVariantStyles.buttonBg,
              color: buttonStyles.color || previewVariantStyles.buttonColor,
              padding: buttonStyles.padding || '16px 32px',
              fontSize: buttonStyles.fontSize || '18px',
              fontWeight: buttonStyles.fontWeight || 'bold',
              fontFamily: buttonStyles.fontFamily || 'inherit',
              border: buttonStyles.border || 'none',
              borderRadius: buttonStyles.borderRadius || '8px',
              cursor: 'pointer',
              width: buttonStyles.width || '100%',
              maxWidth: '400px',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
              transition: 'all 0.2s ease',
              textAlign: buttonStyles.textAlign || 'center',
              margin: buttonStyles.margin || '0'
            }}
            onMouseEnter={(e) => {
              e.target.style.opacity = '0.8';
              e.target.style.transform = 'translateY(-1px)';
              e.target.style.boxShadow = '0 4px 8px rgba(0,0,0,0.15)';
            }}
            onMouseLeave={(e) => {
              e.target.style.opacity = '1';
              e.target.style.transform = 'translateY(0)';
              e.target.style.boxShadow = '0 2px 4px rgba(0,0,0,0.1)';
            }}
            onClick={() => {
              if (element.content?.buttonUrl && element.content?.buttonUrl !== '#') {
                window.open(element.content?.buttonUrl, '_blank');
              }
            }}
          >
            {element.content?.buttonText}
          </button>
        </Box>
      );

    case ELEMENT_TYPES.BUTTON_IMAGE_CALLOUT:
      const getPreviewImageCalloutVariantStyles = (variant) => {
        const v = element.variants?.[variant] || {};
        return {
          background: v.background || '#fff',
          textColor: v.textColor || '#222',
          border: v.border || 'none',
          buttonColor: v.buttonColor || '#1976d2',
          badgeColor: v.badgeColor || '#ffcc00',
          badge2Color: v.badge2Color || '#ffcc00',
        };
      };
      const previewImageCalloutStyles = getPreviewImageCalloutVariantStyles(element.variant);

      // Get custom styles from sub-element styling
      const headlineStyles = element.subElementStyles?.headline || {};
      const subheadlineStyles = element.subElementStyles?.subheadline || {};
      const buttonCalloutStyles = element.subElementStyles?.button || {};
      const containerCalloutStyles = element.styles || {};

      return (
        <Box
          sx={{
            background: containerCalloutStyles.backgroundColor || previewImageCalloutStyles.background,
            color: containerCalloutStyles.color || previewImageCalloutStyles.textColor,
            border: containerCalloutStyles.border || previewImageCalloutStyles.border,
            borderRadius: containerCalloutStyles.borderRadius || '16px',
            mb: 2,
            textAlign: containerCalloutStyles.textAlign || 'center',
            p: { xs: 2, md: 4 },
            boxShadow: '0 2px 12px rgba(0,0,0,0.07)',
            padding: containerCalloutStyles.padding || { xs: 2, md: 4 },
            margin: containerCalloutStyles.margin,
            ...containerCalloutStyles
          }}
        >
          {/* Headline */}
          <Typography
            variant="h6"
            sx={{
              color: headlineStyles.color || previewImageCalloutStyles.textColor,
              fontWeight: headlineStyles.fontWeight || 'bold',
              fontSize: headlineStyles.fontSize || 'inherit',
              fontFamily: headlineStyles.fontFamily || 'inherit',
              textAlign: headlineStyles.textAlign || 'inherit',
              backgroundColor: headlineStyles.backgroundColor || 'transparent',
              padding: headlineStyles.padding || '0',
              margin: headlineStyles.margin || '0 0 16px 0',
              border: headlineStyles.border || 'none',
              borderRadius: headlineStyles.borderRadius || '0',
              mb: 2
            }}
          >
            {element.content?.headline}
          </Typography>

          {/* Image and Badges */}
          <Box sx={{ position: 'relative', display: 'inline-block', mb: 2 }}>
            <img
              src={element.content?.image}
              alt="callout"
              style={{
                maxWidth: '260px',
                width: '100%',
                borderRadius: '12px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.10)',
                background: '#fff',
                marginBottom: 0
              }}
            />
            {/* Badge 1 */}
            {element.content?.badge && (
              <Box
                sx={{
                  position: 'absolute',
                  top: 8,
                  right: 8,
                  background: element.content?.badgeColor || previewImageCalloutStyles.badgeColor,
                  color: '#222',
                  fontWeight: 'bold',
                  borderRadius: '50%',
                  px: 2,
                  py: 1,
                  fontSize: '1rem',
                  boxShadow: '0 2px 6px rgba(0,0,0,0.12)'
                }}
              >
                {element.content?.badge}
              </Box>
            )}
            {/* Badge 2 (for wellamoon) */}
            {element.content?.badge2 && (
              <Box
                sx={{
                  position: 'absolute',
                  bottom: 8,
                  right: 8,
                  background: element.content?.badge2Color || previewImageCalloutStyles.badge2Color,
                  color: '#222',
                  fontWeight: 'bold',
                  borderRadius: '16px',
                  px: 2,
                  py: 1,
                  fontSize: '0.95rem',
                  boxShadow: '0 2px 6px rgba(0,0,0,0.12)'
                }}
              >
                {element.content?.badge2}
              </Box>
            )}
          </Box>

          {/* Subheadline/Description */}
          {element.content?.subheadline && (
            <Typography
              variant="body1"
              sx={{
                color: subheadlineStyles.color || previewImageCalloutStyles.textColor,
                fontWeight: subheadlineStyles.fontWeight || 'inherit',
                fontSize: subheadlineStyles.fontSize || 'inherit',
                fontFamily: subheadlineStyles.fontFamily || 'inherit',
                textAlign: subheadlineStyles.textAlign || 'inherit',
                backgroundColor: subheadlineStyles.backgroundColor || 'transparent',
                padding: subheadlineStyles.padding || '8px',
                margin: subheadlineStyles.margin || '0 0 16px 0',
                border: subheadlineStyles.border || 'none',
                borderRadius: subheadlineStyles.borderRadius || '4px',
                mb: 2,
                p: 1
              }}
            >
              {element.content?.subheadline?.split('\n').map((line, idx) => (
                <span key={idx}>{line}<br /></span>
              ))}
            </Typography>
          )}

          {/* Button */}
          <button
            style={{
              backgroundColor: buttonCalloutStyles.backgroundColor || element.content?.buttonColor || previewImageCalloutStyles.buttonColor,
              color: buttonCalloutStyles.color || '#fff',
              padding: buttonCalloutStyles.padding || '18px 0',
              fontSize: buttonCalloutStyles.fontSize || '1.3rem',
              fontWeight: buttonCalloutStyles.fontWeight || 'bold',
              fontFamily: buttonCalloutStyles.fontFamily || 'inherit',
              border: buttonCalloutStyles.border || 'none',
              borderRadius: buttonCalloutStyles.borderRadius || '8px',
              cursor: 'pointer',
              width: buttonCalloutStyles.width || '100%',
              margin: buttonCalloutStyles.margin || '16px 0',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
              transition: 'all 0.2s ease',
              textAlign: buttonCalloutStyles.textAlign || 'center'
            }}
            onClick={() => {
              if (element.content?.buttonUrl && element.content?.buttonUrl !== '#') {
                window.open(element.content?.buttonUrl, '_blank');
              }
            }}
          >
            {element.content?.buttonText}
          </button>

          {/* Subheadline Icon (e.g., lock) */}
          {element.content?.subheadlineIcon === 'lock' && (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mt: 1 }}>
              <LockIcon fontSize="small" sx={{ color: '#888', mr: 0.5 }} />
              <Typography variant="caption" color="#888">
                {element.content?.subheadline}
              </Typography>
            </Box>
          )}
        </Box>
      );

    case ELEMENT_TYPES.BUTTON_TEXT_VARIANT:
      const getPreviewTextVariantStyles = (variant) => {
        switch (variant) {
          case 'red-shadow':
            return {
              buttonText: 'Push Me',
              buttonUrl: '#',
              buttonColor: '#f5003c',
              textColor: '#fff',
              fontWeight: 'bold',
              fontSize: '2rem',
              borderRadius: '20px',
              boxShadow: '0 6px 0 #a8002a',
              icon: '',
              iconPosition: 'none',
              padding: '24px 0',
              width: '260px',
              margin: '0 auto'
            };
          case 'orange-rounded':
            return {
              buttonText: 'Add To Cart',
              buttonUrl: '#',
              buttonColor: '#f53c00',
              textColor: '#fff',
              fontWeight: 'normal',
              fontSize: '2rem',
              borderRadius: '32px',
              boxShadow: 'none',
              icon: '',
              iconPosition: 'none',
              padding: '24px 0',
              width: '100%',
              margin: '0 auto'
            };
          case 'yellow-gradient':
            return {
              buttonText: 'Claim Your Supply Now',
              buttonUrl: '#',
              buttonColor: 'linear-gradient(180deg, #ffe066 0%, #ffb347 100%)',
              textColor: '#222',
              fontWeight: 'bold',
              fontSize: '2rem',
              borderRadius: '16px',
              boxShadow: 'none',
              icon: 'cart',
              iconPosition: 'left',
              iconColor: '#d4a000',
              padding: '20px 0',
              width: '100%',
              margin: '0 auto'
            };
          default:
            return {
              buttonText: 'Push Me',
              buttonUrl: '#',
              buttonColor: '#f5003c',
              textColor: '#fff',
              fontWeight: 'bold',
              fontSize: '2rem',
              borderRadius: '20px',
              boxShadow: '0 6px 0 #a8002a',
              icon: '',
              iconPosition: 'none',
              padding: '24px 0',
              width: '260px',
              margin: '0 auto'
            };
        }
      };

      const previewButtonTextVariantStyles = getPreviewTextVariantStyles(element.variant);

      // Get custom styles from sub-element styling
      const buttonTextStyles = element.subElementStyles?.button || {};
      const containerTextStyles = element.styles || {};

      return (
        <Box
          sx={{
            backgroundColor: containerTextStyles.backgroundColor || 'transparent',
            padding: containerTextStyles.padding || '20px',
            borderRadius: containerTextStyles.borderRadius || '8px',
            textAlign: containerTextStyles.textAlign || 'center',
            marginBottom: '16px',
            border: containerTextStyles.border || 'none',
            margin: containerTextStyles.margin,
            ...containerTextStyles
          }}
        >
          {/* Button */}
          <button
            style={{
              backgroundColor: buttonTextStyles.backgroundColor || previewButtonTextVariantStyles.buttonColor,
              color: buttonTextStyles.color || previewButtonTextVariantStyles.textColor,
              padding: buttonTextStyles.padding || previewButtonTextVariantStyles.padding,
              fontSize: buttonTextStyles.fontSize || previewButtonTextVariantStyles.fontSize,
              fontWeight: buttonTextStyles.fontWeight || previewButtonTextVariantStyles.fontWeight,
              fontFamily: buttonTextStyles.fontFamily || 'inherit',
              border: buttonTextStyles.border || 'none',
              borderRadius: buttonTextStyles.borderRadius || previewButtonTextVariantStyles.borderRadius,
              cursor: 'pointer',
              width: buttonTextStyles.width || previewButtonTextVariantStyles.width,
              boxShadow: buttonTextStyles.boxShadow || previewButtonTextVariantStyles.boxShadow || '0 2px 4px rgba(0,0,0,0.1)',
              transition: 'all 0.2s ease',
              textAlign: buttonTextStyles.textAlign || 'center',
              margin: buttonTextStyles.margin || previewButtonTextVariantStyles.margin
            }}
            onMouseEnter={(e) => {
              e.target.style.opacity = '0.8';
              e.target.style.transform = 'translateY(-1px)';
            }}
            onMouseLeave={(e) => {
              e.target.style.opacity = '1';
              e.target.style.transform = 'translateY(0)';
            }}
            onClick={() => {
              if (element.content?.buttonUrl && element.content?.buttonUrl !== '#') {
                window.open(element.content?.buttonUrl, '_blank');
              }
            }}
          >
            {element.content?.buttonText || previewButtonTextVariantStyles.buttonText}
          </button>
        </Box>
      );

    case ELEMENT_TYPES.BUTTON_HEADLINE_PRODUCT_IMAGE:
      return (
        <Box
          sx={{
            background: element.content?.background || '#ffffff',
            color: element.content?.textColor || '#333333',
            borderRadius: '16px',
            mb: 2,
            textAlign: 'center',
            p: { xs: 2, md: 4 },
            boxShadow: '0 2px 12px rgba(0,0,0,0.07)'
          }}
        >
          {/* Headline */}
          <Typography
            variant="h6"
            sx={{
              color: element.content?.textColor || '#333333',
              fontWeight: 'bold',
              mb: 2
            }}
          >
            {element.content?.headline || 'Amazing Product Offer'}
          </Typography>

          {/* Product Image */}
          <Box sx={{ mb: 2 }}>
            <img
              src={element.content?.productImage || '/api/placeholder/400/300'}
              alt="Product"
              style={{
                width: '100%',
                maxWidth: '400px',
                height: 'auto',
                borderRadius: '8px',
                border: '2px dashed #e0e0e0'
              }}
            />
          </Box>

          {/* Subheadline */}
          {element.content?.subheadline && (
            <Typography
              variant="body1"
              sx={{
                color: element.content?.textColor || '#333333',
                mb: 2
              }}
            >
              {element.content?.subheadline?.split('\n').map((line, idx) => (
                <span key={idx}>{line}<br /></span>
              ))}
            </Typography>
          )}

          {/* Button */}
          <button
            style={{
              backgroundColor: element.content?.buttonColor || '#4caf50',
              color: '#fff',
              padding: '18px 0',
              fontSize: '1.3rem',
              fontWeight: 'bold',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              width: '100%',
              margin: '16px 0',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
              transition: 'all 0.2s ease'
            }}
            onClick={() => {
              if (element.content?.buttonUrl && element.content?.buttonUrl !== '#') {
                window.open(element.content?.buttonUrl, '_blank');
              }
            }}
          >
            {element.content?.buttonText || 'Get This Offer Now!'}
          </button>
        </Box>
      );


    case ELEMENT_TYPES.CUSTOM_LIST:
      return (
        <Box sx={{ ...element.styles, mb: 2, position: 'relative', '&:hover .element-controls': { opacity: 1 } }}>
          {element.rows.map((row, idx) => (
            <Box key={idx} sx={{ display: 'flex', alignItems: 'center', mb: 1, fontFamily: element.styles?.fontFamily, fontSize: element.styles?.fontSize, color: element.styles?.color }}>
              <Box sx={{ mr: 1 }}>{getListIcon(row.icon, idx)}</Box>
              <span style={{
                ...getPreset(element.textStylePreset || 'paragraph1'),
                fontFamily: getPreset(element.textStylePreset || 'paragraph1')?.fontFamily || element.styles?.fontFamily || 'inherit',
                fontSize: getPreset(element.textStylePreset || 'paragraph1')?.fontSize || element.styles?.fontSize || 'inherit',
                color: getPreset(element.textStylePreset || 'paragraph1')?.color || element.styles?.color || 'inherit',
                fontWeight: getPreset(element.textStylePreset || 'paragraph1')?.fontWeight || 'inherit',
                lineHeight: getPreset(element.textStylePreset || 'paragraph1')?.lineHeight || 'inherit',
                letterSpacing: getPreset(element.textStylePreset || 'paragraph1')?.letterSpacing || 'inherit',
                textAlign: getPreset(element.textStylePreset || 'paragraph1')?.textAlign || 'inherit'
              }}>{row.text}</span>
            </Box>
          ))}
        </Box>
      );

    case ELEMENT_TYPES.CUSTOM_LIST_TWO_LINE:
      return (
        <Box sx={{ ...element.styles, mb: 2, position: 'relative', '&:hover .element-controls': { opacity: 1 } }}>
          {element.rows.map((row, idx) => (
            <Box key={idx} sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
              <Box sx={{ mr: 1, mt: 0.5 }}>{getListIcon(row.icon, idx)}</Box>
              <Box sx={{ flex: 1 }}>
                <Box style={{
                  ...getPreset(element.textStylePreset1 || 'subheading1'),
                  fontFamily: getPreset(element.textStylePreset1 || 'subheading1')?.fontFamily || element.styles?.fontFamily || 'inherit',
                  fontSize: getPreset(element.textStylePreset1 || 'subheading1')?.fontSize || element.styles?.fontSize || 'inherit',
                  color: element.styles?.color || getPreset(element.textStylePreset1 || 'subheading1')?.color || 'inherit',
                  fontWeight: getPreset(element.textStylePreset1 || 'subheading1')?.fontWeight || 'inherit',
                  lineHeight: getPreset(element.textStylePreset1 || 'subheading1')?.lineHeight || 'inherit',
                  letterSpacing: getPreset(element.textStylePreset1 || 'subheading1')?.letterSpacing || 'inherit',
                  textAlign: getPreset(element.textStylePreset1 || 'subheading1')?.textAlign || 'inherit',
                  marginBottom: '4px'
                }}>{row.text1}</Box>
                <Box style={{
                  ...getPreset(element.textStylePreset2 || 'paragraph1'),
                  fontFamily: getPreset(element.textStylePreset2 || 'paragraph1')?.fontFamily || element.styles?.fontFamily || 'inherit',
                  fontSize: getPreset(element.textStylePreset2 || 'paragraph1')?.fontSize || element.styles?.fontSize || 'inherit',
                  color: element.styles?.color || getPreset(element.textStylePreset2 || 'paragraph1')?.color || 'inherit',
                  fontWeight: getPreset(element.textStylePreset2 || 'paragraph1')?.fontWeight || 'inherit',
                  lineHeight: getPreset(element.textStylePreset2 || 'paragraph1')?.lineHeight || 'inherit',
                  letterSpacing: getPreset(element.textStylePreset2 || 'paragraph1')?.letterSpacing || 'inherit',
                  textAlign: getPreset(element.textStylePreset2 || 'paragraph1')?.textAlign || 'inherit'
                }}>{row.text2}</Box>
              </Box>
            </Box>
          ))}
        </Box>
      );

    case ELEMENT_TYPES.TIMELINE_LIST:
      return (
        <Box sx={{
          ...element.styles,
          mb: 2,
          position: 'relative',
          '&:hover .element-controls': { opacity: 1 }
        }}>
          {element.steps.map((step, idx) => (
            <Box key={idx} sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
              <Box sx={{ mr: 2, display: 'flex', alignItems: 'center', flexDirection: 'column', position: 'relative' }}>
                <Box
                  sx={{
                    width: '24px',
                    height: '24px',
                    borderRadius: '50%',
                    backgroundColor: step.dotColor,
                    mb: 1,
                    zIndex: 2,
                    position: 'relative'
                  }}
                />
                {idx < element.steps.length - 1 && (
                  <Box
                    sx={{
                      width: '2px',
                      height: '60px',
                      backgroundColor: '#e0e0e0',
                      position: 'absolute',
                      top: '32px',
                      left: '11px',
                      zIndex: 1
                    }}
                  />
                )}
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography
                  variant="body1"
                  sx={{
                    color: step.stageLabelColor,
                    fontWeight: 'bold',
                    mb: 1
                  }}
                >
                  {step.stageLabel}
                </Typography>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 'bold',
                    mb: 1
                  }}
                >
                  {step.title}
                </Typography>
                <Typography variant="body1">
                  {step.description}
                </Typography>
              </Box>
            </Box>
          ))}
        </Box>
      );

    case ELEMENT_TYPES.PROCESS_STEPS:
      return (
        <Box sx={{
          ...element.styles,
          mb: 2,
          position: 'relative',
          '&:hover .element-controls': { opacity: 1 }
        }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            {element.steps.map((step, idx) => (
              <Box key={idx} sx={{ display: 'flex', alignItems: 'flex-start', gap: 3 }}>
                {/* Image with Numbered Badge */}
                <Box sx={{ flexShrink: 0, width: '150px', position: 'relative' }}>
                  <img
                    src={step.image}
                    alt={`Step ${step.number}`}
                    style={{
                      width: '100%',
                      height: '120px',
                      objectFit: 'cover',
                      borderRadius: '12px',
                      border: '1px solid #e0e0e0'
                    }}
                    onError={(e) => {
                      if (e.target.src !== `https://via.placeholder.com/150x120?text=Step+${step.number}`) {
                        e.target.src = `https://via.placeholder.com/150x120?text=Step+${step.number}`;
                      }
                    }}
                  />

                  {/* Numbered Badge - Top Right Corner */}
                  <Box
                    sx={{
                      position: 'absolute',
                      top: '-8px',
                      right: '-8px',
                      width: '36px',
                      height: '36px',
                      borderRadius: '50%',
                      backgroundColor: step.numberColor || '#1976d2',
                      color: '#fff',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '1.2rem',
                      fontWeight: 'bold',
                      border: '3px solid white',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                      zIndex: 1
                    }}
                  >
                    {step.number}
                  </Box>
                </Box>

                {/* Content Area */}
                <Box sx={{ flex: 1, pt: 1 }}>
                  <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1, color: 'inherit' }}>
                    {step.title}
                  </Typography>
                  <Typography variant="body1" sx={{ color: 'inherit' }}>
                    {step.description}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Box>
        </Box>
      );

    case ELEMENT_TYPES.TEXT_WITH_RIGHT_IMAGE:
      return (
        <Box sx={{
          ...element.styles,
          mb: 2,
          position: 'relative',
          '&:hover .element-controls': { opacity: 1 }
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
            {/* Text Content */}
            <Box sx={{ flex: 1 }}>
              <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'inherit' }}>
                {element.content.text}
              </Typography>
            </Box>

            {/* Image */}
            <Box sx={{ flexShrink: 0, width: '180px' }}>
              <img
                src={element.content.image}
                alt="Right side image"
                style={{
                  width: '100%',
                  height: '120px',
                  objectFit: 'cover',
                  borderRadius: '8px',
                  border: '1px solid #e0e0e0'
                }}
                onError={(e) => {
                  if (e.target.src !== 'https://via.placeholder.com/180x120?text=Image') {
                    e.target.src = 'https://via.placeholder.com/180x120?text=Image';
                  }
                }}
              />
            </Box>
          </Box>
        </Box>
      );

    case ELEMENT_TYPES.FAQ_ACCORDION:
      return (
        <FAQAccordion
          title={element.title}
          items={element.items}
          iconType={element.iconType || 'plusMinus'}
          iconColor={element.iconColor || '#36C3D6'}
          iconSize={element.iconSize || 'large'}
          showIcon={element.showIcon !== false}
          textAlign={element.textAlign || 'left'}
          containerBackgroundColor={element.containerBackgroundColor || 'transparent'}
          questionBackgroundColor={element.questionBackgroundColor || 'transparent'}
          answerBackgroundColor={element.answerBackgroundColor || 'rgba(0, 0, 0, 0.02)'}
          questionTextColor={element.questionTextColor || '#4B329A'}
          answerTextColor={element.answerTextColor || '#4B329A'}
          titleTextColor={element.titleTextColor || 'primary'}
          showDivider={element.showDivider !== false}
          dividerColor={element.dividerColor || '#C1A7F1'}
          itemBorderRadius={element.itemBorderRadius || '4px'}
          itemSpacing={element.itemSpacing || '12px'}
          questionPadding={element.questionPadding || '16px'}
          answerPadding={element.answerPadding || '16px'}
        />
      );

    default:
      return null;
  }
};
